import { useState, useEffect } from 'react';
import {
  List, Box, Typography, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Grid, IconButton, CircularProgress, Card, CardMedia, CardContent, CardActions, Button
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import uz from 'date-fns/locale/uz'; // Импорт локали для узбекского языка
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { fetchClients, fetchMails, fetchProducts } from '../api/github';
import LoadingAnimation from './LoadingAnimation';

/////////////////////////////
const formatDateTime = (dateString) => {
  if (!dateString) return 'Н/Д';
  const date = new Date(dateString);
  return date.toLocaleString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
//////////////////////////////

const isSameDate = (date1, date2) => {
  if (!date1 || !date2) return false;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

const ChecksList = ({ isMobile, refreshTrigger }) => {
  const [checks, setChecks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('nickname');
  const [statusFilter, setStatusFilter] = useState('all');
  const [souvenirFilter, setSouvenirFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadChecks = async () => {
    setLoading(true);
    try {
      const [clients, mails, products] = await Promise.all([fetchClients(), fetchMails(), fetchProducts()]);

      // Создаём мапу продуктов для быстрого поиска
      const productMap = new Map(products.map(p => [p.id, p.isSouvenir || false]));

      // Извлекаем чеки из clients.json
      const clientChecks = clients.flatMap(client =>
        client.orders.flatMap(order => {
          const isSouvenirOrder = (order.products || []).some(p => productMap.get(p.productId));
          return (order.checkImages || []).map(check => {
            const checkObj = typeof check === 'string' ? { url: check, isPaid: false, paidDate: null } : check;
            return {
              url: checkObj.url,
              isPaid: checkObj.isPaid,
              paidDate: checkObj.paidDate,
              clientNickname: client.nickname,
              employeeName: order.employeeName || 'Не указан',
              createdAt: checkObj.paidDate || order.date || new Date().toISOString(),
              isSouvenir: isSouvenirOrder,
              code: client.code || 'Нет кода'
            };
          });
        })
      );

      // Извлекаем чеки из mails.json
      const mailChecks = mails.flatMap(mail => {
        const isSouvenirOrder = (mail.products || []).some(p => productMap.get(p.productId));
        return (mail.checkImages || []).map(check => {
          const checkObj = typeof check === 'string' ? { url: check, isPaid: true, paidDate: null } : check;
          return {
            url: checkObj.url,
            isPaid: checkObj.isPaid,
            paidDate: checkObj.paidDate,
            clientNickname: mail.nickname || 'Почта',
            employeeName: mail.employeeName || 'Не указан',
            createdAt: checkObj.paidDate || mail.createdAt || new Date().toISOString(),
            isSouvenir: isSouvenirOrder,
            code: mail.code || 'Нет кода'
          };
        });
      });

      setChecks([...clientChecks, ...mailChecks]);
    } catch (error) {
      console.error('Чеклар юкланишда хатолик:', error);
      alert('Чеклар юкланишда хатолик юз берди');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChecks();
  }, [refreshTrigger]);

  const handleDownload = (url) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = url.split('/').pop();
    link.click();
  };

  const filteredChecks = checks.filter(check => {
    let matchesSearch = true;
    if (filterField === 'nickname') {
      matchesSearch = check.clientNickname.toLowerCase().includes(searchTerm.toLowerCase());
    } else if (filterField === 'employee') {
      matchesSearch = check.employeeName.toLowerCase().includes(searchTerm.toLowerCase());
    } else if (filterField === 'code') {
      matchesSearch = check.code.toLowerCase().includes(searchTerm.toLowerCase());
    } else if (filterField === 'date' && selectedDate) {
      matchesSearch = isSameDate(check.createdAt, selectedDate);
    }

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'paid' && check.isPaid) ||
      (statusFilter === 'unpaid' && !check.isPaid);

    const matchesSouvenir =
      souvenirFilter === 'all' ||
      (souvenirFilter === 'souvenir' && check.isSouvenir) ||
      (souvenirFilter === 'non-souvenir' && !check.isSouvenir);

    return matchesSearch && matchesStatus && matchesSouvenir;
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uz}>
      <Box>
        {loading ? (
          <LoadingAnimation loading={loading} />
        ) : checks.length === 0 ? (
          <Typography variant="body1" sx={{ mt: 2 }}>
            Чеклар мавжуд эмас
          </Typography>
        ) : (
          <Box>
            <Box sx={{ mb: 2, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Фильтр бўйичи</InputLabel>
                <Select
                  value={filterField}
                  onChange={(e) => {
                    setFilterField(e.target.value);
                    setSearchTerm('');
                    if (e.target.value !== 'date') {
                      setSelectedDate(null);
                    }
                  }}
                  label="Фильтр бўйичи"
                >
                  <MenuItem value="nickname">Ники</MenuItem>
                  <MenuItem value="employee">Ходим</MenuItem>
                  <MenuItem value="code">Код</MenuItem>
                  <MenuItem value="date">Сана</MenuItem>
                </Select>
              </FormControl>
              {filterField === 'date' ? (
                <DatePicker
                  label="Санани танланг"
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  format="dd/MM/yyyy" // Устанавливаем формат DD/MM/YYYY
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                />
              ) : (
                <TextField
                  placeholder="Қидириш..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  size="small"
                />
              )}
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Тўлов ҳолати</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Тўлов ҳолати"
                >
                  <MenuItem value="all">Ҳаммаси</MenuItem>
                  <MenuItem value="paid">Тўланган</MenuItem>
                  <MenuItem value="unpaid">Тўланмаган</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Сувенир</InputLabel>
                <Select
                  value={souvenirFilter}
                  onChange={(e) => setSouvenirFilter(e.target.value)}
                  label="Сувенир"
                >
                  <MenuItem value="all">Ҳаммаси</MenuItem>
                  <MenuItem value="souvenir">Сувенир</MenuItem>
                  <MenuItem value="non-souvenir">Сувенир эмас</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Grid container spacing={2} sx={{ justifyContent: 'space-around' }}>
              {filteredChecks.map((check, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{ position: 'relative', maxWidth: 120 }}>
                    <CardMedia
                      component="img"
                      height="150"
                      image={check.url}
                      alt="Чек"
                      sx={{ objectFit: 'cover' }}
                    />
                    {check.isPaid && (
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(255,255,255,0.7)',
                          color: 'success.main'
                        }}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    )}
                    <CardContent>
                      <Typography variant="subtitle2">
                        Клиент: {check.clientNickname}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ходим: {check.employeeName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Код: {check.code}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Сана: {formatDateTime(check.createdAt)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Сувенир: {check.isSouvenir ? 'Ҳа' : 'Йўқ'}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownload(check.url)}
                        fullWidth
                      >
                        Юклаб олиш
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default ChecksList;